<template>
  <div id="searchBar">
  <nav class="navbar navbar-light bg-light secondary-navbar-light fixed-top fade-in my-0 my-lg-0 px-0" v-cloak  role="navigation">
    <div class="container-fluid d-flex justify-content-between">
      <form v-on:keyup.enter.stop="function(){doSearch()}" autocomplete="off" class="form-inline campaign-filter-form" role="search" id="frmFilters" name="filters" novalidate>
        
        <span v-show="filterPaneVisible" data-toggle="tooltip" title="Close advanced search panel" data-placement="bottom">
          <button class="btn btn-sm btn-indigo mt-2 mt-md-0 mr-2 mr-md-2" 
            data-toggle="collapse" 
            data-target="#filterPane" 
            @click.prevent=""
            type="button"
            >
            <span class="glyphicons glyphicons-remove"></span>
          </button>
        </span>
        <h5 v-if="filterPaneVisible" class="d-inline-block mb-0">Advanced Search <small class="text-muted" v-if="searchTypes.length>1">{{searchTypes[searchTypeIndex].displayName}}</small></h5>

        <!-- email -->
        <div v-show="!filterPaneVisible" class="mr-2 position-relative">
          <template v-if="!suppressSearchTextBox && !isSandboxAccount">
            <input id="email_lookup" type="text" 
              class="form-control form-control-sm" autocomplete="off"
              :class="{'is-invalid': filterEmail && invalidEmail && !isPrimarySearch}" 
              :value="filterEmail" 
              @input="setFilterEmail" 
              :required="!isPrimarySearch" 
              :placeholder="'Lookup ' + lookupPlaceholder" 
              @keyup.delete="function() {if ( !filterEmail ) clearSearchResults();}"
              @change="validateSearchInput(filterEmail)"
              />
            <div class="invalid-tooltip">{{(filterEmail) ? `Must be a valid ${lookupPlaceholder} format` : `${lookupPlaceholder} is required`}}</div>
            <div v-show="filterEmail" class="btn-reset" data-toggle="tooltip" title="Clear search">
              <div @click.stop="resetSearch(true)"><span class="glyphicons glyphicons-remove"></span></div>
            </div>
          </template>
          <template v-else-if="!suppressSearchTextBox">
            <input type="hidden" :value="filterEmail" id="email_lookup" />
            <input type="text" 
              id="txt_persona"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
              class="form-control form-control-sm" 
              :class="{'is-invalid': invalidPersona && !isPrimarySearch}"
              autocomplete="off" :value="currentPersona.name" 
              placeholder="Choose a persona..." 
              @keyup.delete="function() {clearPersona();}" 
              v-on:keypress.prevent=""
            />
            <div class="invalid-tooltip">No lookup persona selected</div>
            <ul class="dropdown-menu">
              <template v-for="(item,idx) in personas">
              <li 
                class="dropdown-item" 
                :class="{'active': currentPersona.id==item.id}"
                @click="setPersona(item)"
              >
                {{item.name}}
                <div class="small font-weight-light"><small>{{item.description}}</small></div>
              </li>
              </template>
            </ul>
            <div v-show="currentPersona.name" class="btn-reset" data-toggle="tooltip" title="Clear search">
              <div @click.stop="clearPersona()"><span class="glyphicons glyphicons-remove"></span></div>
            </div>

          </template>
        </div>
        <!-- /email -->

        <!-- date -->
        <div class="mt-2 mt-sm-0 mr-md-2 date-input-container collapse" :class="{'show': !filterPaneVisible}">
          <input class="form-control form-control-sm dateRangePicker date-input-filter" id="date_filter" autocomplete="off" data-type="date" value="" data-label="Date_filter" />
          <span class="glyphicons glyphicons-calendar calendar-icon"></span>
        </div>
        <!-- /date -->

        <!-- dropdown & input -->
        <div v-if="!filterPaneVisible" class="input-group input-group-sm mr-2 position-relative">
          <div class="input-group-prepend">
            <div id="lookupFilterDropdown"
              class="input-group-text dropdown-toggle"
              data-loading-text="..."
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <div class="dropdown-menu">
                <div class="dropdown-header">Choose lookup filter</div> 
                <template v-for="(item, idx) in filterDropDownItems">
                  <a v-if="(item.id=='subject_keywords' && searchFormat!='sms') || (item.id=='campaign_name' && !overrideFields.campaign_name)"
                    class="dropdown-item" 
                    :class="{'active': search.filterDropdownItem.id==item.id}"
                    :id="'lookupFilter_'+item.id"
                    @click="search.filterDropdownItem=item; focusControl('filterDropDownInputText')"
                  >
                    {{item.displayName}}
                  </a>
                </template>
                <template v-for="(item, idx) in advancedFilters">
                  <template v-if="item.has_options && item.options">
                    <div class="submenu-item">
                      <a class="dropdown-item dropdown-toggle dropright" href="#" 
                        :class="{'active': search.filterDropdownItem.id==item.id}"
                        id="navbarDropdownSubMenuLink" role="button" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                        {{item.displayName}}
                      </a>
                      <div class="dropdown-menu" aria-labelledby="navbarDropdownSubMenuLink" @click="search.filterDropdownItem=item; focusControl('filterDropDownInputText')">
                        <a v-for="opt in item.options" class="dropdown-item" href="#" @click="search.filterDropDownInputText=opt">{{opt}}</a>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <a v-if="item.id!='campaign_name'"
                      class="dropdown-item" 
                      :class="{'active': search.filterDropdownItem.id==item.id}"
                      @click="search.filterDropdownItem=item; focusControl('filterDropDownInputText')"
                    >
                      {{item.displayName}}
                    </a>
                  </template>
                </template>
              </div>
            </div>
          </div>
          <input
            type="text"
            class="form-control form-control-sm last-in-group"
            id="filterDropDownInputText"
            :placeholder="filterDropDownDisplayText"
            v-model="search.filterDropDownInputText"
            :style="search.filterDropdownItem.inputCss"
            autocomplete="off"
            :required="isPrimarySearch && !hasChannelSearchType"
            @keyup.delete="function() {if (search.filterDropDownInputText.length==0) clearSearchResults();}"
          />
          <div class="invalid-tooltip">A value must be included when searching by {{search.filterDropdownItem.displayName}}</div>
        </div>
        <!-- / dropdown & input -->

        <div v-cloak v-if="!filterPaneVisible" class="btn-group mt-2 mt-md-0 mr-2 mr-md-2">
          <button
            v-show="!hideFields.campaignFilters"
            id="btnFilterDropdown"
            class="btn btn-dropdown-default btn-sm dropdown-toggle text-truncate"
            data-loading-text="..."
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
            :disabled="!showPlatformFields.filterCampaign"
            style="width: 150px;"
            type="button"
          >
            {{filterCampaignDisplayText}}
          </button>
          <ul id="btnFilterDropdownItems" class="dropdown-menu">
            <li class="dropdown-item"
              @click="search.filterCampaign=null; search.filterCampaignType=null"
              :class="{'active': search.filterCampaign==null && search.filterCampaignType==null}"
              >No Filter</li>
            <li role="separator" class="dropdown-divider"></li>
            <div class="dropdown-group" style="max-height: calc(100vh - 200px)">
              <li class="dropdown-header">Campaign Type</li>
              <li class="dropdown-item"
                @click="search.filterCampaignType='purpose'; search.filterCampaign='Promotional';"
                :class="{'active': search.filterCampaign=='Promotional' && search.filterCampaignType=='purpose'}"
                >Promotional</li>
              <li class="dropdown-item"
                @click="search.filterCampaignType='purpose'; search.filterCampaign='Transactional';"
                :class="{'active': search.filterCampaign=='Transactional' && search.filterCampaignType=='purpose'}"
                >Transactional</li>
              <template v-if="marketingPrograms.length">
                <li role="separator" class="dropdown-divider"></li>
                <li class="dropdown-header">Marketing Program</li>
                <li
                  class="dropdown-item"
                  v-for="(item,idx) in marketingPrograms"
                  @click="search.filterCampaignType='marketing_program'; search.filterCampaign=item;"
                  :class="{'active': search.filterCampaign==item && search.filterCampaignType=='marketing_program'}"
                >
                  {{item}}
                </li>
              </template>
              <template v-if="marketingStrategies.length">
                <li role="separator" class="dropdown-divider"></li>
                <li class="dropdown-header">Marketing Strategy</li>
                <li
                  class="dropdown-item"
                  v-for="(item,idx) in marketingStrategies"
                  @click="search.filterCampaignType='marketing_strategy'; search.filterCampaign=item;"
                  :class="{'active': search.filterCampaign==item && search.filterCampaignType=='marketing_strategy'}"
                >
                  {{item}}
                </li>
              </template>
            </div>
          </ul>

        </div>
        <div v-cloak v-if="false && !filterPaneVisible && !showPlatformFields.filterCampaign" class="mt-2 mt-md-0 mr-2 mr-md-2" style="min-width: 150px; display: inline-block;">
          &nbsp;
        </div>

        <span v-if="!suppressSearchTextBox" v-show="!filterPaneVisible" data-toggle="tooltip" title="Open advanced search panel" data-placement="bottom">
          <button id="btnAdvancedFilter"
            class="btn btn-sm btn-default mt-2 mt-md-0 mr-2 mr-md-2 d-none d-sm-block" 
            :class="{'btn-outline-indigo ': filterPaneVisible, 'active': filterPaneVisible}"
            data-toggle="collapse" 
            data-target="#filterPane" 
            @click.prevent=""
            type="button"
            :disabled="searchTypes[searchTypeIndex].value=='sms' && false"
            >
            <span class="glyphicons glyphicons-more"></span><span class="glyphicons glyphicons-filter"></span>
          </button>
        </span>

        <button id="btnSearchSubmit" type="submit" v-show="!filterPaneVisible" class="btn btn-sm btn-primary mt-2 mt-md-0 mr-2 mr-md-3" 
          data-toggle="tooltip" title="Search for matching records" data-placement="bottom"
          @click.prevent="doSearch()">
          <span class="glyphicons glyphicons-search"></span>
        </button>

        <button id="btnSearchMobile" aria-haspopup="true" class="form-control btn btn-primary btn-sm mt-2 d-block d-sm-none" 
          data-toggle="tooltip" title="Search for matching records" data-placement="bottom"
          @click.prevent="doSearch()" 
          >
          Search
          <span class="glyphicons glyphicons-search"></span>
        </button>

        <div v-if="!filterPaneVisible" class="btn-group d-flex mt-2 mt-sm-0">
          <button id="btnListView" class="btn btn-sm btn-outline-gray w-100" :class="{active: viewType=='list'}" @click.prevent="setViewType('list')" data-toggle="tooltip" title="Display list view" data-placement="bottom">
            <span class="glyphicons glyphicons-list"></span>
          </button>
          <button id="btnJourneyView" class="btn btn-sm btn-outline-gray w-100" :class="{active: viewType=='journey'}" @click.prevent="setViewType('journey')" data-toggle="tooltip" title="Customer journey view" data-placement="bottom">
            <span class="glyphicons glyphicons-show-thumbnails"></span>
          </button>
        </div>
      </form>

      <div class="d-flex">
        <button id="btnAdvancedReset" v-cloak v-show="filterPaneVisible" aria-haspopup="true" class="btn btn-dropdown-default btn-sm mr-2" 
          data-toggle="tooltip" title="Clear all search values" data-placement="bottom"
          @click.prevent="resetFilters()" 
          >
          Reset
          <span class="glyphicons glyphicons-roundabout"></span>
        </button>
        <button id="btnAdvancedSearch" v-cloak v-show="filterPaneVisible" aria-haspopup="true" class="btn btn-primary btn-sm" 
          data-toggle="tooltip" title="Search for matching records" data-placement="bottom"
          @click.prevent="doSearch(true)" 
          >
          Search
          <span class="glyphicons glyphicons-search"></span>
        </button>
      </div>


      <div v-cloak v-if="!suppressSearchTextBox" v-show="!filterPaneVisible" class="dropdown">
        <button 
          id="btnMoreActions"
          class="btn btn-sm btn-default mt-2 mt-md-0" 
          data-placement="bottom"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          >
          <span class="glyphicons glyphicons-option-vertical"></span>
        </button>

        <button
          v-if="false"
          id="btnOtherActions"
          class="btn btn-dropdown-default btn-sm dropdown-toggle text-truncate"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          style="width: 150px;"
          :disabled="recordsLength == 0"
        >
          Other Actions
        </button>
        <div class="dropdown-menu dropdown-menu-right">
          <a class="dropdown-item" href="#" @click="resetSearch">Reset all search fields</a>
          <div class="dropdown-divider"></div>
          <li class="dropdown-header">Search Settings</li>
          <a class="dropdown-item" href="#" @click="saveDefaultSearch">{{!defaultSearch ? 'Save as' : 'Replace'}} default search</a>
          <a class="dropdown-item" href="#" 
            :class="{disabled: !defaultSearch,'disabled-dropdown-item': !defaultSearch}"
            @click="applyDefaultSearch">Apply default values</a>
          <a class="dropdown-item" href="#" 
            :class="{disabled: !defaultSearch,'disabled-dropdown-item': !defaultSearch}"
            @click="clearDefaultSearch">Remove default search</a>
          <template v-if="privs.DR">
            <div class="dropdown-divider"></div>
            <li class="dropdown-header">Delete Records</li>
            <a class="dropdown-item" 
             v-bind:class="{disabled: !isSelectedEmail,'disabled-dropdown-item': !isSelectedEmail}"
             @click="confirmDelete('selected')" href="#">Delete selected row</a>
            <a class="dropdown-item" 
              v-bind:class="{disabled: recordsLength==0,'disabled-dropdown-item': recordsLength==0}"
              @click="confirmDelete('results')" href="#">Delete all {{(recordsLength>0) ? recordsLength : ''}} results</a>
            <a class="dropdown-item" 
              v-bind:class="{disabled: !resultsKey,'disabled-dropdown-item': !resultsKey}"
              @click="resultsKey && confirmDelete('all')" href="#">Delete all records for 
              <span v-if="resultsKey" class="font-italic">{{resultsKey}}</span>
              <span v-else="resultsKey">searched {{searchTypes[searchTypeIndex].value}}</span>
            </a>
          </template>
          <template v-if="privs.DRB">
            <a class="dropdown-item"  @click="deleteEmailAddresses()" href="#">Delete email addresses</a>
          </template>
          <template v-if="resultsSearchType == 'email' && emailZipDownloadEnabled">
            <div class="dropdown-divider"></div>
            <a class="dropdown-item" 
              v-bind:class="{disabled: !resultsKey,'disabled-dropdown-item': !resultsKey}"
              @click="resultsKey && downloadEmails(false)" href="#">Download all emails for 
              <span v-if="resultsKey" class="font-italic">{{resultsKey}}</span>
              <span v-else="resultsKey">searched {{searchTypes[searchTypeIndex].value}}</span>
            </a>
            <a class="dropdown-item" 
              v-bind:class="{disabled: ! emails?.length > 0,'disabled-dropdown-item': ! emails?.length > 0}"
              @click="emails?.length > 0 && downloadEmails(true)" href="#">Download all emails for search 
            </a>                        
          </template>
        </div>

      </div>

    </div>
    <div id="divDefaultStatus" v-if="defaultStatusText && !filterPaneVisible" class="small container-fluid text-secondary fade-in justify-content-start" style="margin-bottom: -5px;">{{defaultStatusText}}&nbsp;&nbsp;|&nbsp;&nbsp;
      <a href="#" @click="resetSearch">Clear</a> 
      <!--<a href="#" @click="clearDefaultSearch" class="ml-2">Remove default search</a>-->
    </div>
    <div id="filterPane" class="collapse collapsed w-100">
      <form v-on:keyup.enter.stop="function(){doSearch(true)}" 
        id="frmFilterPane"
        class="container-fluid" autocomplete="off" role="search" style="margin-top: 15px; padding-top: 0px;" novalidate>
      <hr style="margin-top: -7px;">
        <div class="form-row pl-1">

          <div class="form-group mr-2 position-relative">
            <label for="emailAddressFilter" class="form-label">&nbsp;{{(smsAllowed) ? "Email or SMS" : "Email Address"}}</label><br/>
            <input id="emailAddressFilter" type="text" class="form-control form-control-sm" autocomplete="off" 
              :value="filterEmail" 
              @input="setFilterEmail" 
              :placeholder="lookupPlaceholder" 
              :required="!isAdvancedPrimarySearch" 
              @keyup.delete="function() {if ( !filterEmail ) clearSearchResults();}"
              @change="validateSearchInput(filterEmail)"/>


            <div v-if="hasAlternateKeys" class="invalid-feedback">Email or <i class="font-weight-semibold">alternate key</i> required</div>
            <div v-else class="invalid-feedback">Email is required</div>
          </div>

          <!-- /email -->

          <!-- date -->
          <div class="form-group mr-2 date-input-container">
            <label for="date_filter2" class="form-label">&nbsp;Lookup Period</label><br/>
            <div class="date-input-container">
              <input class="form-control form-control-sm dateRangePicker date-input-filter" id="date_filter2" autocomplete="off" data-type="date" value="" :placeholder="`{{moment().subtract('2', 'weeks').format('M/D/YY')}}  -  {{moment().add('6','weeks').format('M/D/YY')}}`" data-label="Date_filter" />
              <span class="glyphicons glyphicons-calendar calendar-icon"></span>
            </div>

          </div>
          <!-- /date -->

          <!-- dropdown & input -->
          <div class="form-group mr-2 date-input-container">
            <label for="subjectFilter" class="form-label">&nbsp;Subject Line</label><br/>
            <input id="subjectFilter" 
              v-model="moreFilters.subject_keywords.value" 
              type="text" 
              :disabled="searchFormat=='sms'"
              class="form-control form-control-sm" autocomplete="off" placeholder="Subject line"/>
          </div>
          <!-- / dropdown & input -->

          <div v-if="showPlatformFields.purpose" class="form-group mr-2">
            <label for="campaignTypeFilter" class="form-label">&nbsp;Campaign Type</label><br/>
            <select id="campaignTypeFilter" 
              v-model="moreFilters.purpose.value"
              class="custom-select custom-select-sm" style="width: 165px;">
              <option :value="null" disabled>(Choose Filter)</option>
              <template v-if="moreFilters.purpose.value!=null">
                <hr>
                <option :value="null">All Types</option>
                <hr>
              </template>
              <option value="PROMOTIONAL">Promotional</option>
              <option value="TRANSACTIONAL">Transactional</option>
            </select>
          </div>

          <div v-if="showPlatformFields.marketing_program && marketingPrograms.length" class="form-group mr-2">
            <label for="marketingProgramFilter" class="form-label">&nbsp;Marketing Program</label><br/>
            <select id="marketingProgramFilter" 
              v-model="moreFilters.marketing_program.value"
              class="custom-select custom-select-sm" 
              style="width: 165px;">
              <option :value="null" disabled>(Choose Filter)</option>
              <template v-if="moreFilters.marketing_program.value!=null">
                <hr>
                <option :value="null">All Programs</option>
                <hr>
              </template>
              <option v-for="(item,idx) in marketingPrograms" :value="item">
                {{item}}
              </option>

            </select>
          </div>

          <div v-if="showPlatformFields.marketing_strategy && marketingStrategies.length" class="form-group mr-2">
            <label for="marketingStratFilter" class="form-label">&nbsp;Marketing Strategy</label><br/>
            <select id="marketingStratFilter" 
              v-model="moreFilters.marketing_strategy.value"
              class="custom-select custom-select-sm" 
              style="width: 165px;">
              <option :value="null" disabled>(Choose Filter)</option>
              <template v-if="moreFilters.marketing_strategy.value!=null">
                <hr>
                <option :value="null">All Strategies</option>
                <hr>
              </template>
              <option v-for="(item,idx) in marketingStrategies" :value="item">
                {{item}}
              </option>

            </select>
          </div>


          <div v-if="showPlatformFields.platform_campaign_id" class="form-group mr-2">
            <label for="campaignId" class="form-label">&nbsp;Campaign ID</label><br/>
            <input type="text" v-model="moreFilters.platform_campaign_id.value" class="form-control form-control-sm" id="campaignId" placeholder="Campaign ID"/>
          </div>
          <div v-if="!overrideFields.campaign_name" class="form-group mr-2">
            <label for="campaignNameInput" class="form-label">&nbsp;Campaign Name</label><br/>
            <input type="text" v-model="moreFilters.campaign_name.value" class="form-control form-control-sm" id="campaignNameInput" placeholder="Campaign name"/>
          </div>
          <template v-for="(filter,index) in advancedFilters">
            <template v-if="filter.requireEmail && filter.id!='campaign_name'">
            <div class="form-group mr-2">
              <label :for="'custom_' + filter.name" class="form-label">&nbsp;{{filter.displayName}}</label><br/>
              <input :id="'custom_' + filter.name" v-model="moreFilters[filter.name].value" type="text" class="form-control form-control-sm" :placeholder="filter.displayName" :style="filter.inputCss" :data-input_option="filter.config.input_option"/>
            </div>
            </template>
          </template>
          <div class="form-group mr-2">
            <label for="friendlyFrom" class="form-label">&nbsp;Friendly From</label><br/>
              <input type="text" v-model="moreFilters.from_name_keywords.value" class="form-control form-control-sm" id="friendlyFrom" :disabled="searchFormat=='sms'" placeholder="Friendly from keywords" />
          </div>
          <div class="form-group mr-2">
            <label for="fromEmailFilter" class="form-label">&nbsp;Sender Email</label><br/>
            <select id="fromEmailFilter"
              v-model="moreFilters.from_email.value"
              :disabled="searchFormat=='sms'"
              class="custom-select custom-select-sm" style="width: 165px;">
              <option :value="null" disabled>(Choose Filter)</option>
              <template v-if="moreFilters.from_email.value!=null">
                <hr>
                <option :value="null">All Senders</option>
                <hr>
              </template>
              <option v-for="email in fromEmails">{{email}}</option>
            </select>
          </div>
        </div>
        <template v-if="hasAlternateKeys">
          <hr>
          <h5 class="d-block w-100">Alternate Lookup Keys <span class="glyphicons glyphicons-info-sign text-indigo"
            data-toggle="popover" :data-content="lookupKeyHelpText" data-title="Alternate Lookup Keys"
            style="font-size: .65em;"
            ></span> 
          </h5>
          <div class="form-row pl-1">
          <template v-for="(filter,index) in advancedFilters">
            <template v-if="!filter.requireEmail">
            <div class="form-group mr-2">
              <label :for="'custom_' + filter.name" class="form-label">&nbsp;{{filter.displayName}} <span v-show="filter.method=='INDEPENDENT'" class="text-success glyphicons glyphicons-message-plus small"></span><span v-show="filter.method=='PRIMARY'" class="text-warning glyphicons glyphicons-message-ban small"></span></label><br/>
              <input :id="'custom_' + filter.name" type="text" class="form-control form-control-sm"
                v-model="moreFilters[filter.name].value" :style="filter.inputCss"
                @change="setIsAdvancedPrimarySearch()" :data-input_option="filter.config.input_option"
                :placeholder="filter.displayName"  :data-method="filter.method" v-on:keyup.enter.stop="function(){doSearch(true)}" />
            </div>
            </template>
          </template>
          </div>
        </template>
      </form>
      <div class="handle-collapse no-border d-block w-100" 
        @click="closeFilterPane()">
        <div class="handle-container small">
          <span class="handle">
            <span class="glyphicons glyphicons-chevron-up text-white" style="margin-top: -2px;"></span>
          </span>
        </div>
      </div>        
    </div>
  </nav>
  <div id="pageMasker" class="navbar-fixed-top navbar-2nd fade d-none" style="height: calc(100vh - 95px); background-color:#ccc; opacity: .55; z-index: 220; width: 100%; top: 95px; position: absolute;"></div>
</div>

</template>
<script>
import Vue from 'vue'
import moment from 'moment-timezone'
import {mapState} from 'vuex'
const findIndex = require('lodash/findIndex')
const uniq = require('lodash/uniq')
import DateFormatting from '@/mixins/date-formatting'
import SearchBar from '@/mixins/search-bar'
import {getLocalStorage} from '@/js/sageflo/ui-helpers.js'

export default {
  mixins: [DateFormatting,SearchBar],
  data: function() {return {
    searchTypeIndex: 0,
    searchType: 'email',
    lastSearchWasAdvanced: false,
    overrideFields: {},
    defaultSearch: false,
    defaultStatusText: null,
    search: {
      startDate: null,
      endDate: null,
      filterDateRange: '',
      filterMobile: null,
      filterCampaign: null,
      filterCampaignType: null,
      filterDropdownItem: {
        id: null,
        type: 'standard',
        config: {}
      },
      filterDropDownInputText: '',
      //this is a parameter that can be passed in via query string, and can force a search 
      //for a given channel (for example, a field is passed without an sms number, but passing sms channel will trigger sms search.)
      channel: null, 
    },
    filterDropDownItems: [
      {
        id: 'subject_keywords',
        displayName: 'Subject Line',
        type: 'standard',
        config: {},
      },
      {
        id: 'campaign_name',
        displayName: 'Campaign Name',
        type: 'standard',
        config: {},
      }
    ],
    moreFilters: {
      subject_keywords: {value: null},
      campaign_name: {value: null},
      purpose: {value: null},
      platform_campaign_id: {value: null},
      marketing_program: {value: null},
      marketing_strategy: {value: null},
      from_email: {value: null},
      from_name_keywords: {value: null},
    },
    isAdvancedPrimarySearch: false,
    hasAlternateKeys: false,
    filterPaneVisible: false,
    currentPersona: {}
  }},
  computed: {
    ...mapState({
      uiConfig: (state)=>state.user.uiConfig,
      privs: (state)=>state.user.privs,
      emails: (state)=>state.search.emails,
      loading: (state)=>state.ui.loading,
      activeView: (state)=>state.ui.activeView,
      detailView: (state)=>state.ui.detailView,
      activeAccountIndex: (state)=>state.ui.activeAccountIndex,
      activeSubAccount: (state)=>state.ui.activeSubAccount,
      viewType: (state)=>state.ui.viewType,
      selectedEmail: (state)=>state.search.selectedEmail, 
      personas: (state)=>state.search.personas, 
      resultsSearchType: (state)=>state.search.resultsSearchType,
      filterEmail: (state)=>state.search.filterEmail,
      filterMobile: (state)=>state.search.filterMobile,
      resultsKey: (state)=>state.search.resultsKey,
      qsParams: (state)=>state.search.qsParams,
      authToken: (state)=>state.user.authToken,
      lastSearchFilters: (state)=>state.search.lastSearchFilters,
    }),
    filterDropDownDisplayText: function() {
      if (this.search.filterDropdownItem.id){
        let ret = this.search.filterDropdownItem.displayName;
        if (this.search.filterDropdownItem.type == 'standard') {
          ret += ' keywords';
        }
        return ret;
      } else {
        this.search.filterDropdownItem.id = 'subject_keywords';
        this.search.filterDropdownItem.displayName = 'Subject Line';
        return 'Subject Line keywords';
      }
    },
    filterCampaignDisplayText: function() {
      if (!this.showPlatformFields.filterCampaign) {
        return '(Filters Disabled)';
      } else if (this.search.filterCampaignType==null) {
        return '(Choose Filter)';
      } else {
        return this.search.filterCampaign;
      }
    },
    emailSearchDisabled: function() {
      return Boolean(this.uiConfig.account && (!this.uiConfig.account.config.email_search_disabled));      
    },
    emailZipDownloadEnabled: function() {
      return (this.privs && this.privs.EZ && this.uiConfig.account 
        && this.uiConfig.account.config.email_zip_download_enabled);
    },
    suppressSearchTextBox: function() {
      return Boolean(this.qsParams && this.qsParams.simpleSearch);
    },
    hasChannelSearchType: function() {
      if (this.searchTypes.length > 0) {
        return Boolean(this[this.searchTypes[this.searchTypeIndex].filterParameter]);
      } else {
        return false;
      }
    },
    isSandboxAccount: function() {
      let enabled = Boolean(this.uiConfig.account && this.uiConfig.account.config.sandbox_account)
      if (enabled) {
        this.$store.dispatch("fetchPersonas")
      }
      return enabled
    },
    isPrimarySearch: function() {
      return this.search.filterDropdownItem.method=='PRIMARY' || this.search.filterDropdownItem.method=='INDEPENDENT';
    },
    recordsLength: function() {
      return this.emails.length;
    },
    lookupPlaceholder() {
      if (this.searchTypes.length > 1) {
        return "email or SMS"
      } else {
        return "email"
      }
    },
    fromEmails: function() {

      if (this.uiConfig.from_emails) {

        if (this.activeSubAccount) {
          if (this.uiConfig.from_emails[this.activeSubAccount.sub_account_name]) {
            return uniq(this.uiConfig.from_emails[this.activeSubAccount.sub_account_name]).sort();
          } else {
            return [];
          }
        } else {
          return uniq(this.uiConfig.from_emails['ALL']).sort();
        }
      } else {
        return [];
      }
    },
    marketingPrograms: function() {
      if (this.uiConfig.marketing_programs) {
        if (this.activeSubAccount) {
          if (this.uiConfig.marketing_programs[this.activeSubAccount.sub_account_name]) {
            return uniq(this.uiConfig.marketing_programs[this.activeSubAccount.sub_account_name]).sort();
          } else {
            return [];
          }
        } else {
          return uniq(this.uiConfig.marketing_programs['ALL']).sort();
        }
      } else {
        return [];
      }
    },
    minimumDate: function() {
      let createDate = moment();
      if (this.activeSubAccount) {
        createDate = moment(this.activeSubAccount.created_dt);
        if (this.activeSubAccount.start_dt){
          //if start_dt exists, then it should take precidence over create-dt
          createDate = moment(this.activeSubAccount.start_dt)
        }
        //if now - storage months is after createDate, then the minimum date should be now - storage months
        if (this.activeSubAccount.storage_months!=-1 && this.activeSubAccount.storage_months!=null && moment().subtract(this.activeSubAccount.storage_months, 'months').isAfter(createDate) ) {
          createDate = moment().subtract(this.activeSubAccount.storage_months, 'months');
        }

      } else if (this.uiConfig.sub_accounts) {

        for (let subAccount of this.uiConfig.sub_accounts) {
          let newCreateDate = moment(subAccount.created_dt);
          if (subAccount.start_dt){
            //if start_dt exists, then it should take presidence over created_dt
            newCreateDate = moment(subAccount.start_dt)
          }
          if (subAccount.storage_months!=-1 && subAccount.storage_months!=null && newCreateDate.isBefore(moment().subtract(subAccount.storage_months,'months'))) {
            newCreateDate = moment().subtract(subAccount.storage_months,'months');
          }

          if (newCreateDate.isBefore(createDate)) {            
            createDate = newCreateDate;
          }

        }
        
      }
      return createDate;

    },
    hideFields() {
      let fields = {
        campaignFilters: true,
        campaignInfo: true
      }

      if (this.uiConfig.sub_accounts) {
        for (let subAccount of this.uiConfig.sub_accounts) {
          if (subAccount.source_type=='Responsys' && !subAccount.disabled) {
            fields.campaignFilters = false
            fields.campaignInfo = false
          }
        }        
      }

      return fields
    },
    showPlatformFields: function() {
      let fields = {};
      let platforms = ['Responsys','Demo'];
      let platformFields = {
        Responsys: {
          filterCampaign: true,
          purpose: true,
          marketing_strategy: true,
          marketing_program: true,
          platform_campaign_id: true
        },
        Demo: {
          filterCampaign: true,
          purpose: true,
          marketing_strategy: true,
          marketing_program: true,
          platform_campaign_id: true
        }

      }

      if (this.activeSubAccount) {
        for (let platform of platforms) {
          if (this.activeSubAccount.source_type == platform) {
            Object.assign(fields, platformFields[platform]);  
          }
        }
      } else if (this.uiConfig.sub_accounts) {
        for (let platform of platforms) {
          let idx = findIndex(this.uiConfig.sub_accounts, function(o) { return  o.source_type == platform && !o.disabled })
          if (idx != -1) {
            Object.assign(fields, platformFields[platform])
          }
        }
        
      }
      console.log("showPlatformFields: " + JSON.stringify(fields))
      return fields;
    },
    marketingStrategies: function() {
      if (this.uiConfig.marketing_strategies) {
        if (this.activeSubAccount) {
          if (this.uiConfig.marketing_strategies[this.activeSubAccount.sub_account_name]) {
            return uniq(this.uiConfig.marketing_strategies[this.activeSubAccount.sub_account_name]).sort();
          } else {
            return [];
          }
        } else {
          return uniq(this.uiConfig.marketing_strategies['ALL']).sort();
        }
      } else {
        return [];
      }

    },
    advancedFilters: function() {
      var fields = [];
      this.hasAlternateKeys = false;
      function createInputCss(field){
        let css  = {};
        if (field.config.input_option){
          //We could set "text-transform" with the input_option value, but that seems brittle to me
          if (field.config.input_option == "lowercase"){
            css["text-transform"] = "lowercase";
          } else if (field.config.input_option == "uppercase"){
            css["text-transform"] = "uppercase";
          }
        }
        return css;
      }
      if (this.uiConfig.custom_fields || this.uiConfig.metadata_fields) {
        for (let idx in this.uiConfig.custom_fields) {
          // todo add to top level search filters object
          var mdField = this.uiConfig.custom_fields[idx];
          var currentSearchType = (this.searchType=='email') ? 'mail' : this.searchType;

          //if (mdField.category==currentSearchType) {
          if (mdField.type == 'OVERRIDE') {
            this.overrideFields[mdField.id] = true;
          }

          if (mdField.search_method=="PRIMARY" || mdField.search_method=="INDEPENDENT") {
            this.hasAlternateKeys = true;
          }
          let field ={
            id: mdField.name,
            name: mdField.name,
            column_name: mdField.column_name,
            requireEmail: mdField.search_method == 'DEPENDENT', 
            displayName: mdField.display_name,
            method: mdField.search_method,
            has_options: mdField.has_options,
            options: mdField.has_options ? mdField.options : false,
            type: 'custom',
            config: mdField.config,
          }
          field.inputCss = createInputCss(mdField)
          fields.push(field);
          this.$set(this.moreFilters,mdField.name, Object.assign({value: null},field));
        } 
        //}

        for (let idx in this.uiConfig.metadata_fields) {
          // todo add to top level search filters object

          var mdField = this.uiConfig.metadata_fields[idx];

          if (mdField.search_method!='NONE' && mdField.category==currentSearchType) {
            if (mdField.name == 'campaign_name') {
              this.overrideFields['campaign_name'] = true;
            }
            let field = {
              id: mdField.name,
              name: mdField.name,
              column_name: mdField.column_name,
              requireEmail: true, 
              displayName: mdField.display_name,
              method: mdField.search_method,
              type: 'metadata',
              config: mdField.config,
            }
            field.inputCss = createInputCss(mdField)
            fields.push(field);
            this.$set(this.moreFilters,mdField.name, Object.assign({value: null},field));
            console.log(mdField.name)
          }
        }
      }
      return fields;
    },
    lookupKeyHelpText: function() {
      let searchTypes = (this.smsAllowed) ? "email or SMS" : "email";

      return `Instead of ${searchTypes} as the primary lookup key, you may use an alternate lookup key. <br><br>
        When searching an alternate lookup key, ${searchTypes} may still be included as a search value, but is not a required field.
        `;
    },
    isSelectedEmail(){
      return (this.selectedEmail != null && this.selectedEmail.uuid != null); 
    },
  },
  mounted: function() {
    let that = this;

    this.search.startDate = moment().subtract(29, 'days');
    this.search.endDate = moment();

    $("#filterPane").on('hide.bs.collapse',function () {
      that.filterPaneVisible = false;
      $(document.getElementById('frmFilterPane')).removeClass('was-validated');
      $('#pageMasker').fadeIn(500,'swing').addClass('d-none');
    });

    $("#filterPane").on('show.bs.collapse', function () {
      that.setupFilterPane();
    });

    $("#filterPane").on('shown.bs.collapse', function () {
      // blinks any textboxes with values
      $('#filterPane input[type=text]').each(function(){
        if (($(this).val())) {
          $(this).delay(150).fadeOut(150).fadeIn(200);
        }
      });
    });
    
    $('#pageMasker').on('click', function(){
      $('#filterPane').collapse('hide');
    });

    $('#searchBar').popover({
      //find any elements that have something in data-content
      selector: '[data-toggle="popover"]',
      container: 'body',
      delay: { "show": 500, "hide": 100 },
      html: true,
      trigger: 'hover',
      boundary: 'viewport',
    });

    $('#searchBar').tooltip({selector: '[data-toggle="tooltip"]', html:true, trigger: 'hover', delay: { "show": 500, "hide": 100 }});

  },
  updated: function() {

    let that = this;

    $('#date_filter').daterangepicker({
      singleDatePicker: false,
      minDate: this.minimumDate,
      startDate: this.search.startDate,
      endDate: this.search.endDate,
      timePicker: false,
      autoApply: false,
      autoUpdateInput: true,
      locale: { format: 'MM/DD/YYYY' },
      ranges: {
         'Today': [moment(), moment()],
         'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
         'Last 7 Days': [moment().subtract(6, 'days'), moment()],
         'Last 30 Days': [moment().subtract(29, 'days'), moment()],
         'Last 90 Days': [moment().subtract(89, 'days'), moment()],
         'Last 180 Days': [moment().subtract(179, 'days'), moment()]        
       }
    },(start,end,label)=>{ 
      that.search.startDate = start;
      that.search.endDate = end;
      $('#date_filter2').data('daterangepicker').setStartDate(that.search.startDate);
      $('#date_filter2').data('daterangepicker').setEndDate(that.search.endDate);
    });

    $('#date_filter2').daterangepicker({
      singleDatePicker: false,
      minDate: this.minimumDate,
      startDate: this.search.startDate,
      endDate: this.search.endDate,
      timePicker: false,
      autoApply: false,
      autoUpdateInput: true,
      locale: { format: 'MM/DD/YYYY' },
      ranges: {
         'Today': [moment(), moment()],
         'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
         'Last 7 Days': [moment().subtract(6, 'days'), moment()],
         'Last 30 Days': [moment().subtract(29, 'days'), moment()],
         'Last 90 Days': [moment().subtract(89, 'days'), moment()],
         'Last 180 Days': [moment().subtract(179, 'days'), moment()]        
       }
    },(start,end,label)=>{ 
      that.search.startDate = start;
      that.search.endDate = end;
      $('#date_filter').data('daterangepicker').setStartDate(that.search.startDate);
      $('#date_filter').data('daterangepicker').setEndDate(that.search.endDate);
    });

  },
  methods:{
    deleteEmailAddresses(){
      this.$parent.deleteEmails();    
    },
    doSearch(isAdvancedSearch){
      let isEmailSearch = true
      let isMobileSearch = false
      isAdvancedSearch = (isAdvancedSearch)?true:false;
      console.log(`doSearch ${(isAdvancedSearch) ? '- is advanced' : ''}`)
      this.lastSearchWasAdvanced = isAdvancedSearch;
      let params = {}
      
      if (this.filterEmail) {
        if (this.emailAllowed) {
          if (this.validateEmail(this.filterEmail)) {
            this.invalidEmail = false
            params.email = this.filterEmail
            this.searchFormat = 'email'
            isEmailSearch = true
          } else if (this.smsAllowed && this.validateMobileNumber(this.filterEmail)) {
            this.invalidEmail = false
            params.mobile_number = this.filterMobile.replace(/\+/g, '');
            this.searchFormat = 'sms'
            isEmailSearch = false
            isMobileSearch = true
          } else {
            this.invalidEmail = true
            if (this.search.channel == "sms"){
              isEmailSearch = false;
              isMobileSearch = true;
              this.searchFormat = 'sms';
            } else {
              this.searchFormat = 'email';
            }
            return;
          }
        } else if (this.smsAllowed && this.validateMobileNumber(this.filterEmail)) {
          this.invalidEmail = false
          params.mobile_number = this.filterMobile.replace(/\+/g, '');
          this.searchFormat = 'sms'
          isEmailSearch = false
          isMobileSearch = true
        } else {
          return;
        }
      } else {
        if (this.search.channel == "sms"){
          isEmailSearch = false;
          isMobileSearch = true;
          this.searchFormat = 'sms';
        } else {
          this.searchFormat = 'email';
        }
      }
      console.log(JSON.stringify(params));
      params.start_date = this.search.startDate.format("M/D/YYYY");
      params.end_date = this.search.endDate.format("M/D/YYYY");
      
      function processInputOption(inputOption,value){
        if (inputOption && value){
          if (inputOption == "uppercase"){
            return value.toUpperCase();
          } else if (inputOption == "lowercase"){
            return value.toLowerCase();
          } else {
            return value;
          }
        } else {
          return value;
        }
      }

      if (isAdvancedSearch) {
        var currentSearchType = (this.searchType=='email') ? 'mail' : this.searchType;


        const form = document.getElementById('frmFilterPane');
        if (!form.checkValidity()){
          $(form).addClass('was-validated');
          
          if (document.getElementById('emailAddressFilter').matches(':invalid')) {
            this.focusControl('emailAddressFilter');
          } else if (document.getElementById('filterDropDownInputText').matches(':invalid')) {
            this.focusControl('filterDropDownInputText');
          }
          return;
        } else {
          $(form).removeClass('was-validated');
        }
        for (let key in this.moreFilters) {
          if (this.moreFilters[key] && this.moreFilters[key].value!=null && this.moreFilters[key].value!='') {
            if (this.moreFilters[key].type=='custom' && key!='campaign_name') {
              if (!params.custom_field_filters) params.custom_field_filters = [];
              let element = document.getElementById("custom_" + key)
              let inputOption = element.dataset.input_option;
              params.custom_field_filters.push({name: key, value: processInputOption(inputOption,this.moreFilters[key].value)})
            } else if (this.moreFilters[key].type=="metadata") {
              let element = document.getElementById("custom_" + key);
              let inputOption = element.dataset.input_option;
              if (!params.metadata_filters) params.metadata_filters = [];
              params.metadata_filters.push({name: key, value: processInputOption(inputOption,this.moreFilters[key].value)})
            } else {
              params[key] = this.moreFilters[key].value;  
            }
          }
        }
        this.closeFilterPane();
      } else {
        // validate simple searchbar form
        const form = document.getElementById('frmFilters');
        if (!form.checkValidity()){
          $(form).addClass('was-validated');
          if (document.getElementById('email_lookup').matches(':invalid')) {
            this.focusControl('email_lookup');
          } else if (document.getElementById('filterDropDownInputText').matches(':invalid')) {
            this.focusControl('filterDropDownInputText');
          }
          return;
        } else {
          $(form).removeClass('was-validated');
        }
        
        if (this.search.filterDropDownInputText) {
          let inputOption =  this.search.filterDropdownItem.config.input_option;
          if (this.search.filterDropdownItem.type=='standard') {
            params[this.search.filterDropdownItem.id] = processInputOption(inputOption,this.search.filterDropDownInputText);
          } else if (this.search.filterDropdownItem.type=="metadata") {
            if (!params.metadata_filters) params.metadata_filters = [];
            params.metadata_filters.push({name: this.search.filterDropdownItem.name, value: processInputOption(inputOption,this.search.filterDropDownInputText)})
          } else if (this.search.filterDropdownItem.type=="custom") {
            if (!params.custom_field_filters) params.custom_field_filters = [];
            params.custom_field_filters.push({name: this.search.filterDropdownItem.name, value: processInputOption(inputOption,this.search.filterDropDownInputText)})
          }
        }

        if (this.search.filterCampaign && this.showPlatformFields.filterCampaign) {
          if (this.search.filterCampaignType=='purpose') { // need to convert value to Uppercase for purpose
            params[this.search.filterCampaignType] = this.search.filterCampaign.toUpperCase();
          } else {
            params[this.search.filterCampaignType] = this.search.filterCampaign;  
          }
        }
      }

      if (this.activeSubAccount!=null) {
        params.sub_account=this.activeSubAccount.sub_account_name;
      }
      params.advanced_search = isAdvancedSearch;
      console.log(params);

      let dispatchHandler = !isEmailSearch ? "searchShortMessages" : "searchEmails";

      this.$store.dispatch(dispatchHandler,params);
      
    },
    setPersona(item) {
      this.currentPersona=item;
      this.filterEmail = item.lookup_email
      $('#txt_persona').focus();
      this.doSearch();
    },
    clearPersona() {
      this.currentPersona = {};
      this.resetSearch();
      $('#txt_persona').focus();
    },
    clearSearchResults() {
      // TODO reset emails store to empty array
    },
    resetSearch(setFocus) {
      $(document.getElementById('frmFilterPane')).removeClass('was-validated');
      this.$store.commit("setFilterEmail",null);
      this.$store.commit("setFilterMobile",null);
      this.$store.dispatch("clearResults");
      this.search.filterDropDownInputText = null;
      this.search.filterCampaign=null; 
      this.search.filterCampaignType=null;
      //this.search.startDate = moment().subtract(29, 'days');
      this.search.endDate = moment();

      $('#date_filter').data('daterangepicker').setStartDate(this.search.startDate);
      $('#date_filter').data('daterangepicker').setEndDate(this.search.endDate);
      $('#date_filter2').data('daterangepicker').setStartDate(this.search.startDate);
      $('#date_filter2').data('daterangepicker').setEndDate(this.search.endDate);

      this.defaultStatusText = null;

      if (setFocus) $('#email_lookup').focus()

    },
    saveDefaultSearch() {
      let defaultSearch = {
        searchType: this.searchType,
        primeSearchValue: this.filterEmail,
        filterDropDownInputText: this.search.filterDropDownInputText,
        filterDropdownItemId: this.search.filterDropdownItem.id,
        filterCampaign: this.search.filterCampaign,
        filterCampaignType: this.search.filterCampaignType
      }

      let storageKey = this.uiConfig.user_email + 'settings';
      localStorage.setItem(storageKey,JSON.stringify(defaultSearch));
      this.defaultSearch = JSON.stringify(defaultSearch);
    },
    applyDefaultSearch() {
      let settings = this.defaultSearch;
      if (settings) {
        settings = JSON.parse(settings);

        if (settings.searchTypeIndex) { // first check is if saved search used older format with searchTypeIndex
          this.searchTypeIndex = settings.searchTypeIndex;
          this.searchType = this.searchTypes[this.searchTypeIndex].value
        } else if (settings.searchType) {
          this.searchType = settings.searchType
        } else {
          this.searchType = "email"
        }
        
        if (this.searchType=='email') {
          this.$store.commit("setFilterEmail",settings.primeSearchValue);
        } else if (this.searchType=='sms') {
          this.$store.commit("setFilterMobile",settings.primeSearchValue);
        }
        this.search.filterDropDownInputText = settings.filterDropDownInputText;

        let idx = findIndex(this.filterDropDownItems,{id: settings.filterDropdownItemId}) // check standard filters first
        if (idx==-1) {
          idx = findIndex(this.advancedFilters,{id: settings.filterDropdownItemId}) // next check advanced filters if we didn't find a match
          if (idx!=-1) this.search.filterDropdownItem=this.advancedFilters[idx];
        } else {
          this.search.filterDropdownItem=this.filterDropDownItems[idx]
        }
        
        this.search.filterCampaign = settings.filterCampaign;
        this.search.filterCampaignType = settings.filterCampaignType;

        this.$nextTick(()=>{this.defaultStatusText = 'Currently using default search values'});
      } else {
        this.defaultStatusText = null;
      }
    },
    clearDefaultSearch() {
      this.defaultSearch = null;
      let storageKey = this.uiConfig.user_email + 'settings';
      localStorage.removeItem(storageKey);
      this.defaultStatusText = null;
    },
    resetFilters() {
      this.$store.commit("setFilterEmail",null);
      this.$store.commit("setFilterMobile",null);

      // reset all advanced filters
      for (let key in this.moreFilters) {
        this.moreFilters[key].value = null;
      }
      this.defaultStatusText = null;
    },
    setViewType(viewType){
      localStorage.setItem("viewMode",viewType);
      this.$store.commit("setViewType",viewType);
    },
    closeFilterPane() {
      $('#filterPane').collapse('hide');
    },
    setupFilterPane() {
      this.filterPaneVisible = true;
      $('#pageMasker').fadeIn(500,'swing').removeClass('d-none');

      $(document.getElementById('frmFilters')).removeClass('was-validated');

      // hide any tooltips
      $('[data-toggle="tooltip"]').tooltip('hide');

      // populate value based on filter dropdown setting
      /*if (this.search.filterDropdownItem.id!=null && this.search.filterDropDownInputText) {
        console.log(this.search.filterDropdownItem.id);
        this.moreFilters[this.search.filterDropdownItem.id].value = this.search.filterDropDownInputText
      }*/
      this.setIsAdvancedPrimarySearch();
    },
    setIsAdvancedPrimarySearch() {
      let ret = false;
      try {
        for (let key in this.moreFilters) {
          if ((this.moreFilters[key].type=='custom' || this.moreFilters[key].type=='metadata') && (this.moreFilters[key].value) ) {
            ret = true;
            break;
          }
        }
      } catch(e) {
        console.warn(e);
      }
      this.isAdvancedPrimarySearch = ret;
    },
    confirmDelete(type){
      let that = this;
      let msg = null;
      let filterItem = null;
      let itemSubject = null;

      if (this.filterEmail && this.selectedEmail.channel!='s'){
        filterItem = this.filterEmail;
        itemSubject = this.selectedEmail.subject;
      } else if (this.filterMobile && this.selectedEmail.channel=='s'){
        filterItem = this.filterMobile;
        itemSubject = this.selectedEmail.message;
      }

      if (type == 'selected'){
        if (!this.isSelectedEmail) return;
        let item = this.selectedEmail;
        msg = 'Delete message, <i>' + itemSubject + '</i> sent on ' + this.shortDate(item.sent_dt,this.uiConfig.time_zone) + '?<br/><br/>Are you sure? Click <b>Yes</b> to proceed.';
      } else if (type == 'results'){
        msg = 'Delete all ' + this.recordsLength + ' messages in results? This action cannot be reversed.<br/><br/>Are you sure? Click <b>Yes</b> to proceed.';
      } else if (type == 'all'){
        if (this.activeSubAccount == null){
          msg = 'Delete all messages for <i>' + filterItem + '</i> from the system?';
        } else {
          msg = 'Delete all messages for <i>' + filterItem + '</i> from the <i>' + this.activeSubAccount.sub_account_display_name + '</i> sub-account?';
        }
        msg += ' This action cannot be reversed.<br/><br/>Are you sure?  Click <b>Yes</b> to proceed.';
      } 
      this.$eventBus.$emit("showConfirm",{
        title: 'Confirm Message Delete',
        msg: msg,
        btnOk: 'Yes',
        btnCancel: 'No',
        onConfirm: function(){that.deleteMessages(type);}
      });
    },
    async deleteMessages(type){
      console.log("deleteMessages: " + type)
      let params = {};
      let name = null; 
      let url = null;      
      if (type=="selected"){
        params.uuids = [{u:this.selectedEmail.uuid,d:this.selectedEmail.sd}];
      } else if (type=="results"){
        params.uuids = this.emails.map(e=>{return {u:e.uuid,d:e.sd}});
      }  
      if (this.activeSubAccount){
        params.sub_account_id = this.activeSubAccount.id;
      }
      let eventName = null;
      if (this.searchFormat == 'email'){
        eventName = "email_delete";
        if (type == "selected"){
          name = "Email";
        } else {
          name = "Emails"
        }
        if (type=="all"){
          params.emails = [this.filterEmail];
        }
        params.for_email = this.filterEmail;
        url = "/api/v1/email/delete";

      } else if (this.searchFormat == 'sms'){
        eventName = "sms_delete";
        if (type == "selected"){
          name = "Message";
        } else {
          name = "Messages"
        }
        if (type=="all"){
          params.mobile_numbers = [this.filterMobile.replace(/\+/g, '')];
        }
        url = "/api/v1/sms/delete";
      }
      try {
        const response = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        });
        let result = await response.json();
        if (!response.ok){
          if (response.status == 401){
            window.location.href = "/login";
          } else {
            let msg = `${name} delete error. Status:${response.status} Error: ${result.message}`;
            this.$eventBus.$emit("showStatus",{
              msg: msg,
              title: "Unexpected Error",
              isWarning: true,
            });
          }
        } else {
          //success
          this.$eventBus.$emit("showStatus",{
            msg:`${name} successfully deleted.`,
            title: `${name} deleted`,
          });
          this.doSearch(this.lastSearchWasAdvanced);
          let props = {
            type:type,
          }
          this.$store.dispatch("track",{name:eventName,props:props})
        }
      } catch (err){
        let msg = `${name} delete error. Error: ${err}`;
        this.$eventBus.$emit("showStatus",{
          msg: msg,
          isWarning: true,
        });
        console.log(err);
      }


    },
    async downloadEmails(useFilters){
     this.$eventBus.$emit("showStatus",{
        msg:`Beginning email download. This may take up to a minute or more.`,
        title: `Beginning email download`,
      });
      //Different domain, so we need to pass the auth token as a qs parameter
      let query = `token=${this.authToken}`
      if (useFilters){
        query = query + `&filters=${encodeURIComponent(JSON.stringify(this.lastSearchFilters))}`;
      } else {
        let email = this.filterEmail.toLowerCase().trim();
        query = query + `&email=${encodeURIComponent(email)}`;
      }
      let url = this.uiConfig.download_endpoint + `/api/v1/email/zip?${query}`;
      fetch(url)
        .then(async response => {
          if (!response.ok){
            let result = await response.json();
            let msg = null;
            let title = null;
            if (response.status == 401){
              window.location.href = "/login";
              return;
            } else if (response.status == 404) {
              if (this.filterEmail){
                msg = `No emails were found for ${this.filterEmail}`;
              } else {
                msg = "No emails were found for this search."
              }
              title = "Not found";
            } else {
              msg = `Download error. Status:${response.status} Error: ${result.message}`;
              title = "Unexpected Error";
            }
            this.$eventBus.$emit("showStatus",{
              msg: msg,
              title: title,
              isWarning: true,
            });
          } else {
            //This roundabout and way of downloading the file is so that we can use
            //fetch and check the response for failure/emails not found
            //What this does is download the file, creates a blob,
            //and then creates a link using the blob and clicks the link so it downloads.
            //Then it cleans up the link.          
            let blob = await response.blob()
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            // the filename
            if (this.filterEmail){
              a.download = `${this.filterEmail}_emails.zip`;
            } else {
              a.download = `${moment().format('YYYYMMDD')}_search_emails.zip`
            }
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            this.$eventBus.$emit("showStatus",{
              msg:`Emails download complete.`,
              title: `Download complete`,
            });
          }
        })
        .catch((err) => {
          this.$eventBus.$emit("showStatus",{
            msg: `Unexpected error: ${err}`,
            title: "Unexpected error",
            isWarning: true,
          });
        });
    }
  },
  watch: {
    'search.filterDropDownInputText': {
      handler: function() {
        this.defaultStatusText = null;
      },
      immediate: true      
    },
    'search.filterCampaign': {
      handler: function() {
        this.defaultStatusText = null;
      },
      immediate: true      
    },
    'moreFilters': {
      handler: function(newVal) {
        this.defaultStatusText = null;
      },
      immediate: true
    },
    'filterMobile': {
      handler: function(newVal) {
        this.defaultStatusText = null;
      },
      immediate: true
    },
    'filterEmail': {
      handler: function(newVal) {
        this.defaultStatusText = null;
      },
      immediate: true
    },
    'uiConfig': {
      handler: function(newVal) {
        let storageKey = newVal.user_email + 'settings';
        this.defaultSearch = getLocalStorage(storageKey,false);
      },
      immediate: true
    },
    searchType(newVal) {
      this.search.filterDropDownInputText = null;
      let idx = -1;

      if (newVal=='sms') {
        idx = findIndex(this.filterDropDownItems, function(o) { return o.id == "campaign_name" });        
      } else {
        idx = findIndex(this.filterDropDownItems, function(o) { return o.id == "subject_keywords" });
      }

      if (idx!=-1) {
        this.search.filterDropdownItem = this.filterDropDownItems[idx];
      }
    },
    /*'searchTypeIndex': function(newVal) {
      this.search.filterDropDownInputText = null;
      let idx = -1;

      // make sure, if there is a selected filterType, that it still exists in dropdown list
      if (this.searchTypes[newVal].value=='sms') {
        idx = findIndex(this.filterDropDownItems, function(o) { return o.id == "campaign_name" });        
      } else {
        idx = findIndex(this.filterDropDownItems, function(o) { return o.id == "subject_keywords" });
      }
      if (idx!=-1) {
        this.search.filterDropdownItem = this.filterDropDownItems[idx];
      }
    },*/
    'advancedFilters': function(advancedFilters){
      let qsParams = this.qsParams;
      console.log("Advanced filters changed, qsParams: ", qsParams)
      //not sure what to do about this exactly. Basically just checking
      //if only qs parameter is token, in which case don't set up the search, etc.
      //before this, search would happen when only qs param is token, which is undesirable.
      let doSearch = false;
      if (qsParams){
        let qsKeys = Object.keys(qsParams);
        if (qsKeys.length == 1 && qsKeys[0] == "token"){
          doSearch = false;
        } else {
          doSearch = true;
        }
      }
      if (doSearch){
        if (qsParams.email){
          this.$store.commit("setFilterEmail",qsParams.email);
        } else if (qsParams.sms){
          this.$store.commit("setFilterEmail",qsParams.sms);
        }
        if (qsParams.startDate){
          this.search.startDate = moment(qsParams.startDate,"M/D/YYYY");
        }
        if (qsParams.endDate){
          this.search.endDate = moment(qsParams.endDate,"M/D/YYYY");
        }
        if (qsParams.channel){
          this.search.channel = qsParams.channel;
        }
        if (qsParams.filterSubject || (qsParams.filterValue && qsParams.filterType)){
          let filterName = qsParams.filterType;
          let filterSubject = qsParams.filterSubject;
          //this is backwards compatible with the prior api where filterSubject could be the subject line or the filter value (if filterType was passed)
          //I added filterValue parameter to be more clear
          let filterValue = qsParams.filterValue || qsParams.filterSubject ;
          if (filterSubject && !filterName){
            //just subject line
            this.search.filterDropdownItem = find(this.filterDropdownItems,{id: "subject_keywords"});
            this.search.filterDropDownInputText = filterSubject;
          } else {
            let filter = this.moreFilters[filterName];
            if (filter){
              this.search.filterDropdownItem = filter;
              this.search.filterDropDownInputText = filterValue;
            } else {
              console.log("Filter not found: " + filterName)
            }
          }
        }
        //do search after UI updates
        Vue.nextTick(()=>{
          this.doSearch(false);
        });
      } else {
        this.applyDefaultSearch(); // populate default search values in fields 
      }
    },
  }
}

</script>

<style scoped>
hr {
  width:  100%;
}
.handle-collapse {
  margin-bottom: -10px; 
  height:10px; 
  overflow-y: visible;
  cursor: pointer;
  background-color: var(--indigo);
}

.handle-container {
  text-align: center; 
  padding: 0px; 
  overflow-y: visible; 
  margin-top: -4px;
}

.handle-collapse .handle {
  background-color: var(--indigo);
  border-radius: 15px;
  top: -6px;
  position: relative;
  display: inline-block;
  width: 75px;
}

.handle-collapse:hover {
  background-color: var(--blue);
  transition: all .3s;
}

.handle-collapse:hover .handle {
  background-color: var(--blue);
  transition: all .3s;
}

select.custom-select-sm {
  font-size: 100%;
  padding-bottom: .1rem;
  padding-top: .1rem;
}


select.custom-select-sm:focus {
  border-color: var(--cyan);
  box-shadow: 0 0 0 0.2rem rgba(63,175,204,.25);
}

input::placeholder {
  font-size: 13px;
}

input.form-control-sm {
  min-height: 31px;
}

.input-group input.last-in-group {
  border-top-right-radius: .2rem !important;
  border-bottom-right-radius: .2rem !important;
}

.control-group-tight {
  max-width: 165px;
}

.disabled-dropdown-item{
  cursor:not-allowed;
  background-color: white;
}

.submenu-item {
  position: relative;
}

.submenu-item > a.dropdown-item.dropdown-toggle.dropright:after, .submenu-item > a.dropdown-item.dropdown-toggle.dropright.active:after {
    font-family: 'Glyphicons Regular';
    content: "\E224";
    border: none !important;
    float: right;
}

.submenu-item > .dropdown-menu {
    top: 0;
    left: 100%;
    margin-top: 0px;
    margin-left: 0px;
}

/* makes sub-menu S open on hover */
.submenu-item:hover > .dropdown-menu {
  display: block;
}


#email_lookup {
  padding-right: 30px;
}

.was-validated #email_lookup.form-control:invalid, .was-validated .form-control:valid, #email_lookup.form-control.is-invalid {
  background-image: none;
}

.btn-reset {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 28px;
  text-align: center;
  padding: .2rem;
  cursor:  pointer;
  color: var(--gray);
  background-color: transparent;
  z-index: 5;
}

.btn-reset:hover {
  background-color: var(--gray);
  border-radius: 1rem;
  color: var(--light);
}

</style>